/**
 * Dataset do CRM (Task 13, brief 3.4) e seus seletores.
 *
 * Endereço CANÔNICO (Task 13, limpeza de arquitetura): promovido de
 * `apps/plataforma/src/crm/dataset.ts` para `packages/crm`. A Edge Function
 * `supabase/functions/crm-runtime` e o app importam daqui; o arquivo antigo em
 * `apps/plataforma/src/crm/dataset.ts` reexporta deste módulo.
 *
 * Seis fontes, cada uma com o seu estado declarado (`live` vs
 * `awaiting_contract`), no mesmo vocabulário do dashboard, do catálogo e dos
 * pedidos: fonte ausente é dito, nunca convertido em "nenhum registro".
 *
 * Os seletores aqui são a fonte ÚNICA de "quais registros compõem este
 * recorte": o pipeline, a lista de orçamentos, a fila de tarefas e a visão 360
 * leem daqui, então a contagem de uma tela não discorda da outra. Puro e sem
 * I/O.
 */
import { effectiveQuoteStatus, isClosedStage, LEAD_STAGES, quoteTotals, taskDueState } from "./pipeline.js";
/** Estado inicial honesto: nada buscado ainda (não é "CRM vazio"). */
export function emptyCrmDataset() {
    return {
        customers: [],
        companies: [],
        opportunities: [],
        quotes: [],
        tasks: [],
        segments: [],
        sources: {
            customers: "awaiting_contract",
            companies: "awaiting_contract",
            opportunities: "awaiting_contract",
            quotes: "awaiting_contract",
            tasks: "awaiting_contract",
            segments: "awaiting_contract"
        }
    };
}
/**
 * Dataset do CRM a partir do payload de `GET /api/crm/360` — o caminho
 * CANÔNICO (correção da revisão da Iteração 2, finding 1).
 *
 * A diferença em relação às seis listas globais não é de formato, é de QUEM
 * decide o recorte: aqui o cliente foi resolvido no servidor por
 * `resolve_customer_by_identity` (0020) e as cinco fontes já vêm filtradas por
 * aquele id. A tela deixa de espelhar a resolução de identidade e passa a
 * exibir o que o runtime persistente afirma sobre o mesmo cliente.
 *
 * As fontes entram como `live` porque o runtime respondeu: lista vazia aqui
 * significa, de verdade, "este cliente não tem esse registro" — e é por isso
 * que a resposta precisa ter vindo deste payload, não de uma falha silenciosa.
 */
export function crmDatasetFromPayload(payload) {
    return {
        customers: [payload.customer],
        companies: payload.company ? [payload.company] : [],
        opportunities: payload.opportunities,
        quotes: payload.quotes,
        tasks: payload.tasks,
        segments: payload.segments,
        sources: {
            customers: "live",
            companies: "live",
            opportunities: "live",
            quotes: "live",
            tasks: "live",
            segments: "live"
        }
    };
}
export function isCrmSourceAwaitingContract(dataset, source) {
    return dataset.sources[source] === "awaiting_contract";
}
/** `true` quando NENHUMA fonte do CRM respondeu neste ambiente. */
export function isCrmUnavailable(dataset) {
    return Object.keys(dataset.sources).every((source) => dataset.sources[source] === "awaiting_contract");
}
// --- Cadastro unificado ----------------------------------------------------
export function crmCustomerById(dataset, customerId) {
    return dataset.customers.find((customer) => customer.id === customerId) ?? null;
}
/** Só dígitos, comparando pelo sufixo (DDI entra ou não dependendo do canal). */
export function identityPhoneKey(value) {
    if (!value)
        return "";
    const digits = value.replace(/\D+/g, "");
    return digits.length > 11 ? digits.slice(-11) : digits;
}
export function identityEmailKey(value) {
    return value ? value.trim().toLowerCase() : "";
}
/**
 * Cliente canônico a partir de um identificador de canal — espelho de leitura
 * de `resolve_customer_by_identity` (migration 0020) sobre o cadastro que a
 * rota publicou.
 *
 * É o que permite a visão 360 ser do MESMO cliente mesmo quando a conversa
 * guarda só o telefone do inbound: o 360 não tem um "cliente da conversa" e um
 * "cliente do pedido". Nunca cria cadastro — resolução é leitura.
 */
export function resolveCrmCustomer(dataset, identity) {
    if (identity.customerId) {
        const direct = crmCustomerById(dataset, identity.customerId);
        if (direct)
            return direct;
    }
    const phone = identityPhoneKey(identity.phone ?? identity.customerId);
    if (phone) {
        const byPhone = dataset.customers.find((customer) => identityPhoneKey(customer.phone) === phone ||
            customer.identities.some((item) => item.kind === "whatsapp_phone" && identityPhoneKey(item.value) === phone));
        if (byPhone)
            return byPhone;
    }
    const email = identityEmailKey(identity.email);
    if (email) {
        const byEmail = dataset.customers.find((customer) => identityEmailKey(customer.email) === email ||
            customer.identities.some((item) => item.kind === "email" && identityEmailKey(item.value) === email));
        if (byEmail)
            return byEmail;
    }
    return null;
}
export function crmCompanyById(dataset, companyId) {
    if (!companyId)
        return null;
    return dataset.companies.find((company) => company.id === companyId) ?? null;
}
/** Empresa do cliente (B2B) com os seus contatos; `null` em pessoa física. */
export function companyOfCustomer(dataset, customer) {
    if (!customer)
        return null;
    return crmCompanyById(dataset, customer.companyId);
}
// --- Pipeline --------------------------------------------------------------
export function opportunitiesOfCustomer(dataset, customerId) {
    return dataset.opportunities
        .filter((opportunity) => opportunity.customerId === customerId)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}
export function opportunityById(dataset, opportunityId) {
    return dataset.opportunities.find((opportunity) => opportunity.id === opportunityId) ?? null;
}
export function opportunitiesByStage(dataset, stage) {
    return dataset.opportunities
        .filter((opportunity) => opportunity.stage === stage)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}
export function openOpportunities(dataset) {
    return dataset.opportunities.filter((opportunity) => !isClosedStage(opportunity.stage));
}
/**
 * Funil por estágio, em ordem estável.
 *
 * `withoutValue` existe porque a soma da coluna só conta o que tem valor
 * declarado: sem esse contador, uma coluna com cinco negociações sem valor
 * apareceria como "R$ 0,00" e o operador leria "nada em jogo" (D-13.8).
 */
export function pipelineColumns(dataset) {
    return LEAD_STAGES.map((stage) => {
        const opportunities = opportunitiesByStage(dataset, stage);
        return {
            stage,
            opportunities,
            valueCents: opportunities.reduce((sum, opportunity) => sum + (opportunity.valueCents ?? 0), 0),
            withoutValue: opportunities.filter((opportunity) => opportunity.valueCents === null).length
        };
    });
}
/** Linha do tempo do estágio: do evento mais antigo ao mais recente. */
export function opportunityTimeline(opportunity) {
    return opportunity.events
        .map((event, index) => ({ event, index }))
        .sort((a, b) => a.event.occurredAt.localeCompare(b.event.occurredAt) || a.index - b.index)
        .map((entry) => entry.event);
}
// --- Orçamentos ------------------------------------------------------------
export function quotesOfCustomer(dataset, customerId) {
    return dataset.quotes
        .filter((quote) => quote.customerId === customerId)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}
export function quotesOfOpportunity(dataset, opportunityId) {
    return dataset.quotes.filter((quote) => quote.opportunityId === opportunityId);
}
export function quoteById(dataset, quoteId) {
    return dataset.quotes.find((quote) => quote.id === quoteId) ?? null;
}
/** Proposta em aberto = ainda pode virar venda (rascunho ou enviada, no prazo). */
export function openQuotes(dataset, now) {
    return dataset.quotes.filter((quote) => {
        const status = effectiveQuoteStatus(quote, now);
        return status === "draft" || status === "sent";
    });
}
/**
 * Orçamento com situação efetiva e totais derivados dos itens.
 *
 * Os totais vêm de `quoteTotals` (regra pura) em vez das colunas agregadas: se
 * o resumo gravado e os itens discordarem, a tela mostra o que os itens dizem,
 * porque é o item que o cliente recebeu na proposta.
 */
export function quoteView(quote, now) {
    return {
        quote,
        status: effectiveQuoteStatus(quote, now),
        totals: quoteTotals(quote.items.map((item) => ({
            quantity: item.quantity,
            unitPriceCents: item.unitPriceCents,
            unitCostCents: item.unitCostCents
        })), { discountCents: quote.discountCents, shippingCents: quote.shippingCents })
    };
}
// --- Tarefas e follow-ups --------------------------------------------------
export function tasksOfCustomer(dataset, customerId) {
    return dataset.tasks
        .filter((task) => task.customerId === customerId)
        .sort((a, b) => a.dueAt.localeCompare(b.dueAt));
}
export function tasksOfOpportunity(dataset, opportunityId) {
    return dataset.tasks.filter((task) => task.opportunityId === opportunityId);
}
export function tasksByStatus(dataset, status) {
    return dataset.tasks.filter((task) => task.status === status).sort((a, b) => a.dueAt.localeCompare(b.dueAt));
}
/**
 * Fila de follow-up: tarefas abertas em ordem de prazo, atrasadas primeiro.
 * É a MESMA ordenação que a tela exibe e que o 360 do cliente usa, para o
 * operador não ver duas prioridades diferentes para a mesma tarefa.
 */
export function taskQueue(dataset, now) {
    const order = { overdue: 0, today: 1, upcoming: 2, closed: 3 };
    return dataset.tasks
        .filter((task) => task.status === "open")
        .map((task) => ({ task, due: taskDueState(task, now) }))
        .sort((a, b) => order[a.due] - order[b.due] || a.task.dueAt.localeCompare(b.task.dueAt));
}
export function overdueTasks(dataset, now) {
    return taskQueue(dataset, now)
        .filter((row) => row.due === "overdue")
        .map((row) => row.task);
}
